import { useState, useMemo } from "react";
import { Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchableChildSelect from "../common/SearchableChildSelect";
import SaringanTab from "./SaringanTab";
import AktivitiTab from "./AktivitiTab";
import KesihatanTab from "./KesihatanTab";
import { useModuleAccess } from "../../hooks/useModuleAccess";

export default function DailyLogForm({ 
  formData, 
  setFormData, 
  onSubmit, 
  onCancel,
  editingLog,
  childrenList,
  filteredChildren,
  addMode,
  setAddMode,
  selectedClassForBulk,
  setSelectedClassForBulk,
  availableClasses,
  photoFile,
  setPhotoFile,
  healthPhotoFile,
  setHealthPhotoFile,
  stoolPhotoFile,
  setStoolPhotoFile,
  activeFormTab,
  setActiveFormTab,
  bulkSubmitting,
  user,
  excludedChildIds = [],
  setExcludedChildIds,
  excludeSearch = "",
  setExcludeSearch
}) {
  // Kawalan module: masa log 24 jam (ON: 7AM-6AM, OFF: 7AM-6PM)
  const { isModuleOn } = useModuleAccess(user?.role);
  const use24hTimes = isModuleOn('log_time_24h');

  // Canvas initialization removed as physical condition section was removed


  // Canvas drawing functions removed as physical condition section was removed

  const updateFormField = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const toggleTimeSelection = (field, time) => {
    const currentTimes = formData[field]?.times || [];
    const newTimes = currentTimes.includes(time)
      ? currentTimes.filter(t => t !== time)
      : [...currentTimes, time];
    
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        times: newTimes
      }
    }));
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* Add Mode Toggle - Only show when adding new */}
      {!editingLog && user && (user.role === 'admin' || user.role === 'guru') && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Mod Tambah</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAddMode("individual")}
              className={`flex-1 py-2 px-4 rounded-xl font-medium text-sm transition-all ${
                addMode === "individual"
                  ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              👤 Individu
            </button>
            <button
              type="button"
              onClick={() => setAddMode("class")}
              className={`flex-1 py-2 px-4 rounded-xl font-medium text-sm transition-all ${
                addMode === "class"
                  ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              👥 Seluruh Kelas
            </button>
          </div>
        </div>
      )}

      {/* Individual Mode - Child Selection */}
      {addMode === "individual" ? (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Kanak-kanak</label>
          <SearchableChildSelect
            childrenList={filteredChildren}
            value={formData.child_id}
            onChange={(value) => updateFormField('child_id', value)}
            placeholder="Cari atau pilih kanak-kanak..."
            disabled={!!editingLog}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Pilih Kelas</label>
            <select
              value={selectedClassForBulk}
              onChange={(e) => {
                setSelectedClassForBulk(e.target.value);
                setExcludedChildIds?.([]);
              }}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
              required={addMode === "class"}
            >
              <option value="">Pilih kelas</option>
              <option value="all">🌟 Semua Kelas ({childrenList.length} kanak-kanak)</option>
              {availableClasses.filter(c => c !== "Pre-school").map(className => {
                const count = childrenList.filter(c => c.class_name === className).length;
                return (
                  <option key={className} value={className} disabled={count === 0}>
                    {className} ({count} kanak-kanak)
                  </option>
                );
              })}
            </select>
          </div>

          {/* Exclusion Section */}
          {selectedClassForBulk && setExcludedChildIds && (
            <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
              <label className="block text-sm font-semibold text-orange-700 mb-2">
                🚫 Kecualikan Kanak-kanak (Pilihan)
              </label>
              <p className="text-xs text-orange-600 mb-3">
                Cari dan pilih kanak-kanak yang tidak perlu menerima rekod ini
              </p>
              
              {/* Search input */}
              <div className="relative mb-3">
                <input
                  type="text"
                  placeholder="Cari nama untuk dikecualikan..."
                  value={excludeSearch}
                  onChange={(e) => setExcludeSearch?.(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-orange-200 rounded-lg text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-200"
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              
              {/* Children list for exclusion */}
              <div className="max-h-40 overflow-y-auto space-y-1 bg-white rounded-lg p-2">
                {(() => {
                  const targetChildren = selectedClassForBulk === "all" 
                    ? childrenList 
                    : childrenList.filter(c => c.class_name === selectedClassForBulk);
                  
                  const searchedChildren = excludeSearch 
                    ? targetChildren.filter(c => 
                        c.name?.toLowerCase().includes(excludeSearch.toLowerCase()) ||
                        c.nickname?.toLowerCase().includes(excludeSearch.toLowerCase())
                      )
                    : targetChildren;
                  
                  return searchedChildren.map(child => (
                    <label 
                      key={child.id} 
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        excludedChildIds.includes(child.id) 
                          ? 'bg-red-100 text-red-700' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={excludedChildIds.includes(child.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setExcludedChildIds([...excludedChildIds, child.id]);
                          } else {
                            setExcludedChildIds(excludedChildIds.filter(id => id !== child.id));
                          }
                        }}
                        className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-400"
                      />
                      <span className={`text-sm ${excludedChildIds.includes(child.id) ? 'line-through' : ''}`}>
                        {child.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {selectedClassForBulk === "all" ? child.class_name : ''}
                      </span>
                    </label>
                  ));
                })()}
              </div>
              
              {/* Exclusion summary */}
              {excludedChildIds.length > 0 && (
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-red-600">
                    {excludedChildIds.length} kanak-kanak dikecualikan
                  </p>
                  <button
                    type="button"
                    onClick={() => setExcludedChildIds([])}
                    className="text-xs text-red-600 hover:text-red-800 underline"
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          {selectedClassForBulk && (
            <p className="text-xs text-blue-600">
              ✓ {(() => {
                const targetChildren = selectedClassForBulk === "all" 
                  ? childrenList 
                  : childrenList.filter(c => c.class_name === selectedClassForBulk);
                return targetChildren.length - excludedChildIds.length;
              })()} kanak-kanak akan menerima rekod ini
            </p>
          )}
        </div>
      )}

      {/* Date & Attendance */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-blue-800 mb-1">Tarikh</label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => updateFormField('date', e.target.value)}
            className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-base box-border"
            style={{ maxWidth: '100%' }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-blue-800 mb-1">Kehadiran</label>
          <select
            value={formData.attendance}
            onChange={(e) => updateFormField('attendance', e.target.value)}
            className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-base"
          >
            <option value="Hadir">Hadir</option>
            <option value="Tidak Hadir">Tidak Hadir</option>
            <option value="Cuti">Cuti</option>
          </select>
        </div>
      </div>

      {/* Form Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { id: 'saringan', label: '📋 Saringan', color: 'purple' },
          { id: 'aktiviti', label: '🍼 Aktiviti', color: 'pink' },
          { id: 'kesihatan', label: '🏥 Kesihatan', color: 'red' }
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveFormTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${
              activeFormTab === tab.id 
                ? `bg-${tab.color}-100 text-${tab.color}-700 border-b-2 border-${tab.color}-500` 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* SARINGAN TAB */}
      {activeFormTab === 'saringan' && (
        <SaringanTab formData={formData} updateFormField={updateFormField} />
      )}

      {/* AKTIVITI TAB */}
      {activeFormTab === 'aktiviti' && (
        <AktivitiTab
          formData={formData}
          updateFormField={updateFormField}
          toggleTimeSelection={toggleTimeSelection}
          stoolPhotoFile={stoolPhotoFile}
          setStoolPhotoFile={setStoolPhotoFile}
          use24hTimes={use24hTimes}
        />
      )}

      {/* KESIHATAN TAB */}
      {activeFormTab === 'kesihatan' && (
        <KesihatanTab
          formData={formData}
          updateFormField={updateFormField}
          setHealthPhotoFile={setHealthPhotoFile}
        />
      )}

      {/* Submit Buttons */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Batal
        </Button>
        <Button type="submit" disabled={bulkSubmitting} className="bg-blue-500 hover:bg-blue-600 text-white">
          {bulkSubmitting ? 'Menyimpan...' : editingLog ? 'Kemaskini' : 'Simpan'}
        </Button>
      </div>
    </form>
  );
}
